import { useTranslation } from 'react-i18next';
import type { DryRunResponse } from '../types/retentionAdmin';

interface DryRunResultPanelProps {
  result: DryRunResponse;
}

/**
 * DryRunResultPanel — dry-run 결과(matched/total/ratio) 시각화
 * ratio > maxDeleteRatio 면 경고 색상 + 배지
 */
export function DryRunResultPanel({ result }: DryRunResultPanelProps) {
  const { t } = useTranslation();

  const pct = Math.round(result.ratio * 1000) / 10; // 소수 1자리
  const maxPct = Math.round(result.maxDeleteRatio * 100);
  const barWidth = Math.min(result.ratio / result.maxDeleteRatio, 1) * 100;
  const exceeds = result.ratioExceedsGuard;

  return (
    <div
      role="region"
      aria-label={t('retentionAdmin.dryRun.title')}
      style={{
        border: `1px solid ${exceeds ? 'var(--color-warning)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-4)',
        background: exceeds ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
        marginTop: 'var(--spacing-3)',
      }}
    >
      <p
        style={{
          fontWeight: 700,
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-2)',
        }}
      >
        {t('retentionAdmin.dryRun.title')}
      </p>

      <p
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: exceeds ? 'var(--color-warning)' : 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-1)',
        }}
      >
        {t('retentionAdmin.dryRun.matched', {
          matched: result.matched.toLocaleString(),
        })}
      </p>

      <p
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        {t('retentionAdmin.dryRun.ratio', {
          total: result.total.toLocaleString(),
          percent: `${pct}%`,
        })}
      </p>

      {/* 비율 진행 막대 */}
      <div
        style={{
          position: 'relative',
          height: 8,
          background: 'var(--color-surface-elevated)',
          borderRadius: 'var(--radius-full, 9999px)',
          overflow: 'hidden',
          marginBottom: 'var(--spacing-2)',
        }}
        aria-hidden="true"
      >
        <div
          style={{
            height: '100%',
            width: `${barWidth}%`,
            background: exceeds ? 'var(--color-danger)' : 'var(--color-brand-primary)',
            borderRadius: 'var(--radius-full, 9999px)',
            transition: 'width 0.3s ease',
          }}
        />
      </div>

      {/* 가드 배지 */}
      {exceeds ? (
        <p
          role="alert"
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-danger)',
            fontWeight: 600,
          }}
        >
          {t('retentionAdmin.dryRun.guardExceeded', { max: `${maxPct}%` })}
        </p>
      ) : (
        <p
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-success, #16a34a)',
          }}
        >
          {t('retentionAdmin.dryRun.guardOk', { max: `${maxPct}%` })}
        </p>
      )}
    </div>
  );
}
